import { TranslationKey } from '../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../types/translation/Translator';
import CreateTriggerModal from './Modals/CreateTriggerModal';
import { TriggerContext } from '../../../../context/TriggerContext';
import { Trigger, getTextForTrigger, DefaultTriggerIds, TriggerEntityType } from '../../../../types/trigger';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import DeleteTriggerModal from './Modals/DeleteTriggerModal';
import { LotContext } from '../../../../context/LotContext';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { CompanyContext } from '../../../../context/CompanyContext';
import Table, { TableHeader } from '../../../components/Table';
import { TriggerFilter } from '../../../../VentoryUI/components/filters/TriggerFilter';
import { UserContext } from '../../../../context/UserContext';
import UpdateTriggerModal from './Modals/UpdateTriggerModal';
import StatusLabel from '../../../../VentoryUI/components/common/StatusLabel/StatusLabel';
import { VentoryColor } from '../../../../VentoryUI/util/color.util';
import { TriggerMutations, UpdateTriggerResponse, UpdateTriggerVariables } from '../../../../graphql/trigger.graphql';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import useMutation from '../../../../hooks/useMutation';
import { TagContext } from '../../../../context/TagContext';
import { Button, ButtonVariant, CreateButton, DeleteButton } from '../../../components/Button';
import { SearchFilter } from '../../../components/Filter';

export default function TriggersSettingsPane() {
  const { currentCompany } = useContext(CompanyContext);
  const { triggers, triggersLoading, setTriggers } = useContext(TriggerContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { lots } = useContext(LotContext);
  const { customFields } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { tags } = useContext(TagContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);

  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);

  const [selectedTrigger, setSelectedTrigger] = useState<Trigger | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allTriggers = useMemo(() => {
    const triggerValues = [...triggers.values()];
    if (isAdmin) return triggerValues;

    // This authorization filtering will have to be updated
    return triggerValues.filter(trigger => {
      if (trigger.entityType !== TriggerEntityType.task) return isAdmin;
      const triggerStockLocationIds = trigger.stockLocationIds.filter(stockLocationId =>
        stockLocations.has(stockLocationId),
      );
      return triggerStockLocationIds.every(id =>
        hasStockLocationRole(currentCompany.id, id, StockLocationRole.STOCK_LOCATION_MANAGER),
      );
    });
  }, [triggers, isAdmin, hasStockLocationRole, stockLocations, currentCompany]);

  const [items, setItems] = useState(allTriggers);

  const triggerTexts = useMemo(() => {
    const texts = new Map();

    for (const [key, value] of triggers) {
      texts.set(
        key,
        getTextForTrigger(value, productMasterData, stockLocations, lots, customFields, companyUsers, tags),
      );
    }

    return texts;
  }, [triggers, productMasterData, stockLocations, lots, customFields, companyUsers, tags]);

  const headers: TableHeader<Trigger>[] = [
    {
      key: 'trigger',
      name: 'Trigger',
      text: (item: Trigger) => triggerTexts.get(item.id) || 'Unknown Trigger',
      weight: 10,
      position: 'start',
    },
    {
      key: 'status',
      name: t(TranslationKey.status).singular.label,
      sortValue: item => item.enabled,
      weight: 2,
      text: item => (
        <StatusLabel
          color={
            item.enabled
              ? {
                  color: VentoryColor.green700,
                  backgroundColor: VentoryColor.green50,
                }
              : {
                  color: VentoryColor.grey700,
                  backgroundColor: VentoryColor.grey200,
                }
          }
          text={item.enabled ? t(TranslationKey.enabled).singular.label : t(TranslationKey.disabled).plural.label}
        />
      ),
    },
  ];

  const [update, { loading }] = useMutation<UpdateTriggerResponse, UpdateTriggerVariables>(TriggerMutations.update, {
    onCompleted: res => {
      res.updateTrigger.forEach(trigger => triggers.set(trigger.id, new Trigger(trigger)));
      setTriggers(new Map(triggers));
    },
  });

  const handleUpdate = async () => {
    const input = [...triggers.values()]
      .filter(trigger => selected.has(trigger.id))
      .map(trigger => {
        trigger.enabled = false;
        return trigger;
      });

    try {
      await update({
        variables: {
          triggers: input,
        },
      });
    } catch (e) {}
  };

  const filter = new TriggerFilter(triggerTexts);

  return (
    <div className='content-x content-y-screen flex h-full flex-grow flex-col gap-8'>
      {createModalOpen && <CreateTriggerModal open={createModalOpen} setOpen={setCreateModalOpen} />}
      {updateModalOpen && selectedTrigger && (
        <UpdateTriggerModal
          open={updateModalOpen}
          setOpen={setUpdateModalOpen}
          trigger={selectedTrigger}
          onClose={() => {
            setSelectedTrigger(null);
            setUpdateModalOpen(false);
          }}
        />
      )}
      {deleteModalOpen && (
        <DeleteTriggerModal open={deleteModalOpen} setOpen={setDeleteModalOpen} idsToDelete={selected} />
      )}

      <div className='grid gap-2'>
        <p className='page-title'>{t(TranslationKey.triggerSettingsScreenTitle).singular.upper}</p>
        <p className='subtitle'>{t(TranslationKey.triggerSettingsScreenSubtitle).singular.upper}</p>
      </div>

      <div className='flex flex-grow flex-col gap-4'>
        <div className='flex justify-between gap-4'>
          <SearchFilter
            placeholder={t(TranslationKey.filterTriggers).singular.label}
            items={allTriggers}
            setItems={setItems}
            filter={filter}
          />

          <div className='flex gap-2'>
            {selected.size ? (
              <>
                <Button
                  variant={ButtonVariant.secondary}
                  text={t(TranslationKey.disable).singular.upper}
                  onClick={handleUpdate}
                  loading={loading}
                />
                <DeleteButton onClick={() => setDeleteModalOpen(true)} />
              </>
            ) : null}
            <CreateButton onClick={() => setCreateModalOpen(true)} />
          </div>
        </div>

        <Table
          loading={triggersLoading}
          items={items}
          headers={headers}
          filter={filter}
          onSelected={items => setSelected(new Set(items))}
          disabledCheckbox={item => new Set<string>(Object.values(DefaultTriggerIds)).has(item.id)}
          onClick={item => {
            setUpdateModalOpen(true);
            setSelectedTrigger(item);
          }}
        />
      </div>
    </div>
  );
}
